import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppLayout from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import CustomerFormDialog from "../components/CustomerFormDialog";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { UserPlus, Pencil, Trash2, Receipt } from "lucide-react";
import CustomerQuotesDialog from "../components/CustomerQuotesDialog";

export default function CustomersPage() {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  // iter 148: which customer's quote-history dialog is currently open.
  const [quotesCustomer, setQuotesCustomer] = useState(null);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers");
      setCustomers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("customers.loaded_failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleAdd = () => {
    setEditingCustomer(null);
    setDialogOpen(true);
  };

  const handleEdit = (c) => {
    setEditingCustomer(c);
    setDialogOpen(true);
  };

  const handleSubmit = async (payload) => {
    try {
      if (editingCustomer) {
        await api.put(`/customers/${editingCustomer.id}`, payload);
        toast.success(t("customers.updated"));
      } else {
        await api.post("/customers", payload);
        toast.success(t("customers.created"));
      }
      setDialogOpen(false);
      fetchCustomers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("customers.operation_failed"));
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(t("customers.delete_confirm", { name: c.name }))) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success(t("customers.deleted"));
      fetchCustomers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("customers.delete_failed"));
    }
  };

  return (
    <AppLayout>
      <div data-testid="customers-page" className="max-w-6xl space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">{t("customers.title")}</h1>
          <Button onClick={handleAdd} data-testid="add-customer-button" className="rounded-lg bg-[#1677ff] hover:bg-[#0958d9] transition-colors">
            <UserPlus size={14} className="mr-1.5" /> {t("customers.add")}
          </Button>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto no-scrollbar">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent bg-gray-50 border-b border-gray-200">
                  <TableHead className="text-[12px] uppercase tracking-wide text-gray-700 font-bold">{t("customers.customer_name")}</TableHead>
                  <TableHead className="text-[12px] uppercase tracking-wide text-gray-700 font-bold">{t("customers.export_rep")}</TableHead>
                  <TableHead className="text-[12px] uppercase tracking-wide text-gray-700 font-bold">{t("customers.contact_email")}</TableHead>
                  <TableHead className="text-[12px] uppercase tracking-wide text-gray-700 font-bold">{t("customers.contact_person")}</TableHead>
                  <TableHead className="text-[12px] uppercase tracking-wide text-gray-700 font-bold">{t("customers.address")}</TableHead>
                  <TableHead className="text-[12px] uppercase tracking-wide text-gray-700 font-bold">{t("customers.destination_port")}</TableHead>
                  <TableHead className="text-[12px] uppercase tracking-wide text-gray-700 font-bold">{t("customers.payment_method")}</TableHead>
                  <TableHead className="text-[12px] uppercase tracking-wide text-gray-700 font-bold">{t("customers.currency")}</TableHead>
                  <TableHead className="text-[12px] uppercase tracking-wide text-gray-700 font-bold text-right">{t("customers.price_coefficient")}</TableHead>
                  <TableHead className="text-[12px] uppercase tracking-wide text-gray-700 font-bold text-right">{t("customers.sea_freight_40")}</TableHead>
                  <TableHead className="text-[12px] uppercase tracking-wide text-gray-700 font-bold text-right">{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!loading && customers.map((c) => (
                  <TableRow key={c.id} className="border-b border-gray-50" data-testid={`customer-row-${c.id}`}>
                    <TableCell className="text-sm font-semibold text-gray-900">{c.name}</TableCell>
                    <TableCell className="text-sm text-gray-500">{c.export_rep || "-"}</TableCell>
                    <TableCell className="text-sm text-gray-500">{c.contact_email || "-"}</TableCell>
                    <TableCell className="text-sm text-gray-500">{c.contact_person || "-"}</TableCell>
                    <TableCell className="text-sm text-gray-500 max-w-[200px] truncate">{c.address || "-"}</TableCell>
                    <TableCell className="text-sm text-gray-500">{c.destination_port || "-"}</TableCell>
                    <TableCell className="text-sm text-gray-500">{c.payment_method || "-"}</TableCell>
                    <TableCell className="font-mono text-xs text-gray-600">{c.currency}</TableCell>
                    <TableCell className="font-mono text-sm text-right text-gray-800">{c.price_coefficient != null ? Number(c.price_coefficient).toFixed(2) : "1.00"}</TableCell>
                    <TableCell className="font-mono text-sm text-right text-gray-800">{c.sea_freight_per_container != null ? `$${Number(c.sea_freight_per_container).toFixed(2)}` : "-"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-3">
                        {/* iter 148: quote history button - opens dialog listing
                            every quotation tied to this customer. */}
                        <button data-testid={`customer-quotes-${c.id}`} onClick={() => setQuotesCustomer(c)} title={t("customers.view_quotes_history")} className="text-gray-400 hover:text-brand-600 transition-colors">
                          <Receipt size={14} strokeWidth={1.75} />
                        </button>
                        <button data-testid={`edit-customer-${c.id}`} onClick={() => handleEdit(c)} className="text-gray-400 hover:text-brand-600 transition-colors">
                          <Pencil size={14} strokeWidth={1.75} />
                        </button>
                        <button data-testid={`delete-customer-${c.id}`} onClick={() => handleDelete(c)} className="text-gray-400 hover:text-red-500 transition-colors">
                          <Trash2 size={14} strokeWidth={1.75} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && customers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center text-sm text-gray-400 py-8" data-testid="customers-empty-state">No customers yet</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <CustomerFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        customer={editingCustomer}
        onSubmit={handleSubmit}
      />

      <CustomerQuotesDialog
        open={!!quotesCustomer}
        onOpenChange={(v) => !v && setQuotesCustomer(null)}
        customer={quotesCustomer}
      />
    </AppLayout>
  );
}
